import QueryString from 'qs';
import sanitizeHtml from 'sanitize-html';
import { IApiRequestConfig, TApiResponse } from '../types';
import { constructFdBody } from '../../../../utils/constructFdBody';
import { isArray, isPlainObject } from 'lodash';

export const fetchApiAsync = async (
  config: IApiRequestConfig,
  jwt?: string
): Promise<Partial<TApiResponse>> => {
  const { path, method = 'GET', query, body, headers = {} } = config;

  const queryString = query ? '?' + QueryString.stringify(query) : '';

  const url = `${path}${queryString}`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);

  let preparedBody: string | FormData | undefined;
  let contentType = 'application/json';

  if (body && method !== 'GET') {
    const { data, meta, files } = constructFdBody(body);
    if (Object.keys(files).length) {
      contentType = '';
      const formData = new FormData();
      for (const [key, value] of Object.entries(files)) {
        formData.append(key, value);
      }
      if (data) {
        if (isPlainObject(data) || isArray(data)) {
          try {
            const dataString = JSON.stringify(data);
            formData.append('data', dataString);
          } catch {
            console.error(`Request body.data must be array o plain object!`);
          }
        }
      }
      if (meta) {
        if (isPlainObject(meta) || isArray(meta)) {
          try {
            const dataString = JSON.stringify(meta);
            formData.append('meta', dataString);
          } catch {
            console.error(`Request body.meta must be array o plain object!`);
          }
        }
      }
      preparedBody = formData;
    } else {
      try {
        preparedBody = JSON.stringify({ data, meta });
      } catch {
        console.error(`Request body.data & body.meta must be array o plain object!`);
      }
    }
  }

  const fetchOptions: RequestInit = {
    method,
    headers: {
      ...{ ...headers, client: document.location.hostname },
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
      ...(contentType ? { 'content-type': contentType } : {}),
    },
    signal: controller.signal,
    body: preparedBody,
  };

  try {
    const response = await fetch(url, fetchOptions);
    clearTimeout(timeoutId);

    // Получаем заголовки
    const headersObj: Record<string, string> = {};
    response.headers.forEach((value, key) => {
      headersObj[key] = value;
    });

    // Пробуем распарсить JSON, если тело есть
    const parseError = {
      status: 400,
      message: 'Response format error',
      details: 'No valid response body received',
    };

    let responseObj: TApiResponse;
    const text = await response.text();
    try {
      responseObj = text ? JSON.parse(text) : null;
      if (!responseObj)
        return {
          error: parseError,
        };
    } catch {
      return {
        error: parseError,
      };
    }

    if (!response.ok || responseObj.error) {
      return {
        error: {
          status: response.status || responseObj.error.status,
          message: response.statusText || responseObj.error.message,
          details:
            typeof responseObj.error.details === 'string'
              ? sanitizeHtml(responseObj.error.details)
              : responseObj.error.details,
        },
      };
    }

    return responseObj;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      return {
        error: {
          status: 408,
          message: 'Request timeout',
          details: 'Request exceeded 30 seconds',
        },
      };
    }
    throw error;
  }
};
